import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import BrowserUtils from '@zvuk-b2b/react-uikit/toolkit/BrowserUtils';
import useDropdownPlacement from 'application/hooks/useDropdownPlacement';
import {
  ADDRESSES_DROPDOWN_MAX_HEIGHT,
  ADDRESSES_DROPDOWN_MIN_HEIGHT,
  ADDRESSES_DROPDOWN_PREFERRED_HEIGHT,
  ADDRESSES_DROPDOWN_VIEWPORT_GAP,
  DATA_TEST_IDS,
} from '../consts';
import { TEXTS } from '../texts';
import type { AddressReportPointsSelectProps } from './types';
import type { ReportAddressTagType } from '../types';

const getNormalizedValue = (value: string) => value.trim().toLowerCase();
const DROPDOWN_GAP = 4;
const DROPDOWN_HORIZONTAL_PADDING = 32;
const APPLIED_TAGS_GAP = 8;
const APPLIED_TAG_HORIZONTAL_PADDING = 16;
const APPLIED_TAG_ICON_WIDTH = 16;
const APPLIED_TAG_ICON_GAP = 4;

const getTextWidth = (text: string) => Math.ceil(BrowserUtils.getTextWidth(text).width);

const getAppliedTagWidth = (tag: ReportAddressTagType) =>
  getTextWidth(tag.title) + APPLIED_TAG_HORIZONTAL_PADDING + APPLIED_TAG_ICON_WIDTH + APPLIED_TAG_ICON_GAP;

const getAppliedTagsWidth = (tags: ReportAddressTagType[]) =>
  tags.reduce((result, tag) => result + getAppliedTagWidth(tag), 0);

const getButtonWidth = (text: string, horizontalPadding = 0) => getTextWidth(text) + horizontalPadding;

const getVisibleSelectedTags = ({
  availableWidth,
  isExpanded,
  selectedTags,
}: {
  availableWidth: number;
  isExpanded: boolean;
  selectedTags: ReportAddressTagType[];
}) => {
  if (isExpanded || selectedTags.length <= 1 || availableWidth <= 0) {
    return selectedTags;
  }

  const clearButtonWidth = getButtonWidth(TEXTS.AUDIO_REPORT_ADDRESSES_CLEAR_TAGS);
  const allItemsCount = selectedTags.length + 1;
  const allTagsWidth =
    getAppliedTagsWidth(selectedTags) + clearButtonWidth + APPLIED_TAGS_GAP * Math.max(allItemsCount - 1, 0);

  if (allTagsWidth <= availableWidth) {
    return selectedTags;
  }

  for (let visibleCount = selectedTags.length - 1; visibleCount > 0; visibleCount -= 1) {
    const visibleTags = selectedTags.slice(0, visibleCount);
    const hiddenCount = selectedTags.length - visibleCount;
    const toggleButtonWidth = getButtonWidth(
      TEXTS.AUDIO_REPORT_ADDRESSES_MORE_TAGS(hiddenCount),
      APPLIED_TAG_HORIZONTAL_PADDING
    );
    const renderedItemsCount = visibleTags.length + 2;
    const renderedWidth =
      getAppliedTagsWidth(visibleTags) +
      toggleButtonWidth +
      clearButtonWidth +
      APPLIED_TAGS_GAP * Math.max(renderedItemsCount - 1, 0);

    if (renderedWidth <= availableWidth) {
      return visibleTags;
    }
  }

  return selectedTags.slice(0, 1);
};

export const useAddressReportPointsSelectViewModel = ({
  addresses,
  isLoading,
  selectedAddressIds,
  tags,
  onChange,
}: AddressReportPointsSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tagFilterRef = useRef<HTMLDivElement>(null);
  const tagFilterButtonRef = useRef<HTMLButtonElement>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const [isExactSearch, setIsExactSearch] = useState(true);
  const [search, setSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isAppliedTagsExpanded, setIsAppliedTagsExpanded] = useState(false);
  const [appliedTagsAvailableWidth, setAppliedTagsAvailableWidth] = useState(0);

  useEffect(() => {
    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (!containerRef.current?.contains(target)) {
        setIsDropdownOpen(false);
        setIsTagFilterOpen(false);

        return;
      }

      const isTagFilterClick =
        Boolean(tagFilterRef.current?.contains(target)) || Boolean(tagFilterButtonRef.current?.contains(target));

      if (!isTagFilterClick) {
        setIsTagFilterOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => document.removeEventListener('pointerdown', handleDocumentPointerDown);
  }, []);

  const dropdownState = useDropdownPlacement({
    containerRef,
    isOpen: isDropdownOpen,
    gap: DROPDOWN_GAP,
    maxHeight: ADDRESSES_DROPDOWN_MAX_HEIGHT,
    minHeight: ADDRESSES_DROPDOWN_MIN_HEIGHT,
    preferredHeight: ADDRESSES_DROPDOWN_PREFERRED_HEIGHT,
    viewportGap: ADDRESSES_DROPDOWN_VIEWPORT_GAP,
  });

  useLayoutEffect(() => {
    const containerElement = containerRef.current;

    if (!isDropdownOpen || !containerElement) {
      setAppliedTagsAvailableWidth(0);

      return undefined;
    }

    const updateAppliedTagsWidth = () => {
      const nextWidth = Math.max(containerElement.getBoundingClientRect().width - DROPDOWN_HORIZONTAL_PADDING, 0);

      setAppliedTagsAvailableWidth(nextWidth);
    };

    updateAppliedTagsWidth();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateAppliedTagsWidth);

    resizeObserver?.observe(containerElement);
    window.addEventListener('resize', updateAppliedTagsWidth);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateAppliedTagsWidth);
    };
  }, [isDropdownOpen]);

  const selectedAddressIdSet = useMemo(() => new Set(selectedAddressIds), [selectedAddressIds]);

  const selectedTagSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  const filteredTags = useMemo(() => {
    const normalizedSearch = getNormalizedValue(tagSearch);

    if (!normalizedSearch) {
      return tags;
    }

    return tags.filter((tag) => getNormalizedValue(tag.title).includes(normalizedSearch));
  }, [tagSearch, tags]);

  const filteredAddresses = useMemo(() => {
    const normalizedSearch = getNormalizedValue(search);

    return addresses.filter((address) => {
      const searchableAddressText = `${address.address} ${address.comment || ''}`;
      const isMatchedBySearch =
        !normalizedSearch || getNormalizedValue(searchableAddressText).includes(normalizedSearch);

      if (!isMatchedBySearch || selectedTagIds.length === 0) {
        return isMatchedBySearch;
      }

      const addressTagIds = new Set(address.tags.map((tag) => tag.id));

      if (isExactSearch) {
        return selectedTagIds.every((tagId) => addressTagIds.has(tagId));
      }

      return selectedTagIds.some((tagId) => addressTagIds.has(tagId));
    });
  }, [addresses, isExactSearch, search, selectedTagIds]);

  const selectedTags = useMemo(
    () => selectedTagIds.flatMap((tagId) => tags.find((tag) => tag.id === tagId) || []),
    [selectedTagIds, tags]
  );
  const visibleSelectedTags = useMemo(
    () =>
      getVisibleSelectedTags({
        availableWidth: appliedTagsAvailableWidth,
        isExpanded: isAppliedTagsExpanded,
        selectedTags,
      }),
    [appliedTagsAvailableWidth, isAppliedTagsExpanded, selectedTags]
  );
  const hiddenSelectedTagsCount = Math.max(selectedTags.length - visibleSelectedTags.length, 0);
  const filteredAddressIds = useMemo(() => filteredAddresses.map((address) => address.id), [filteredAddresses]);

  const selectedCount = selectedAddressIds.length;
  const totalCount = addresses.length;
  const hasSelectedAddresses = selectedCount > 0;
  const hasUnselectedAddresses = filteredAddressIds.some((addressId) => !selectedAddressIdSet.has(addressId));
  const dropdownId = DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_DROPDOWN;

  const onDropdownToggle = useCallback(() => {
    setIsDropdownOpen((currentValue) => {
      const nextValue = !currentValue;

      if (!nextValue) {
        setIsTagFilterOpen(false);
      }

      return nextValue;
    });
  }, []);

  const onTagFilterToggle = useCallback(() => {
    setIsTagFilterOpen((currentValue) => !currentValue);
  }, []);

  const onSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const onTagSearchChange = useCallback((value: string) => {
    setTagSearch(value);
  }, []);

  const onExactSearchChange = useCallback((value: boolean) => {
    setIsExactSearch(value);
  }, []);

  const onAddressChange = useCallback(
    (addressId: string, value: boolean) => {
      setIsTagFilterOpen(false);

      if (value) {
        onChange(selectedAddressIds.includes(addressId) ? selectedAddressIds : [...selectedAddressIds, addressId]);

        return;
      }

      onChange(selectedAddressIds.filter((currentAddressId) => currentAddressId !== addressId));
    },
    [onChange, selectedAddressIds]
  );

  const onTagChange = useCallback((tagId: string, value: boolean) => {
    if (value) {
      setSelectedTagIds((currentTagIds) => (currentTagIds.includes(tagId) ? currentTagIds : [...currentTagIds, tagId]));

      return;
    }

    setSelectedTagIds((currentTagIds) => currentTagIds.filter((currentTagId) => currentTagId !== tagId));
  }, []);

  const onSelectAll = useCallback(() => {
    onChange(Array.from(new Set([...selectedAddressIds, ...filteredAddressIds])));
  }, [filteredAddressIds, onChange, selectedAddressIds]);

  const onReset = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const onClearTags = useCallback(() => {
    setSelectedTagIds([]);
    setTagSearch('');
    setIsAppliedTagsExpanded(false);
  }, []);

  const onAppliedTagRemove = useCallback((tagId: string) => {
    setSelectedTagIds((currentTagIds) => currentTagIds.filter((currentTagId) => currentTagId !== tagId));
  }, []);

  const onAppliedTagsToggle = useCallback(() => {
    setIsAppliedTagsExpanded((currentValue) => !currentValue);
  }, []);

  return {
    containerRef,
    tagFilterButtonRef,
    tagFilterRef,
    dropdownId,
    dropdownState,
    filteredAddresses,
    filteredTags,
    hasSelectedAddresses,
    hasUnselectedAddresses,
    hiddenSelectedTagsCount,
    isAppliedTagsExpanded,
    isDropdownOpen,
    isExactSearch,
    isLoading,
    isTagFilterOpen,
    search,
    selectedAddressIdSet,
    selectedCount,
    selectedTagSet,
    selectedTags,
    tagSearch,
    totalCount,
    visibleSelectedTags,
    onAddressChange,
    onAppliedTagRemove,
    onAppliedTagsToggle,
    onClearTags,
    onDropdownToggle,
    onExactSearchChange,
    onReset,
    onSearchChange,
    onSelectAll,
    onTagChange,
    onTagFilterToggle,
    onTagSearchChange,
  };
};
